/**
 * VALUATION STORE INITIALIZER
 * 
 * Inicializa o Redux valuation store com snapshot padrão na primeira carga
 * Garante que Dashboard sempre tenha dados para exibir
 * 
 * @version 1.0.0
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { ValuationEngine } from '../core/ValuationEngine.v2';
import { setSnapshot } from '../store/slices/valuationSlice';
import { createCompanyAssumptions } from '../store/slices/valuationSlice';

export function ValuationStoreInitializer() {
  const dispatch = useDispatch<AppDispatch>();
  const snapshot = useSelector((state: RootState) => state.valuation.snapshot);
  const macroAssumptions = useSelector((state: RootState) => state.valuation.macroAssumptions);
  const equityBridge = useSelector((state: RootState) => state.valuation.equityBridge);
  const discounts = useSelector((state: RootState) => state.valuation.discounts);
  const stake = useSelector((state: RootState) => state.valuation.stake);
  const scenarios = useSelector((state: RootState) => state.valuation.scenarios);
  const companyParams = useSelector((state: RootState) => state.valuation.companyParams);
  const baseYear = useSelector((state: RootState) => state.valuation.baseYear);

  useEffect(() => {
    // Só inicializa se snapshot ainda estiver null
    if (snapshot === null) {
      console.log('[ValuationStoreInitializer] 🚀 Inicializando snapshot padrão...');

      try {
        // Usar cenário BASE como padrão
        const setimaScenario = scenarios.setima.base;
        const studios555Scenario = scenarios.studios555.base;

        // ✅ USAR FUNÇÃO HELPER DINÂMICA
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          companyParams.setima.beta,
          companyParams.setima.capex_pct_receita,
          companyParams.setima.nwc_pct_delta_receita,
          macroAssumptions,
          baseYear  // ✅ ANOS DINÂMICOS
        );

        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          companyParams.studios555.beta,
          companyParams.studios555.capex_pct_receita,
          companyParams.studios555.nwc_pct_delta_receita,
          macroAssumptions,
          baseYear  // ✅ ANOS DINÂMICOS
        );

        const engine = new ValuationEngine();
        const initialSnapshot = engine.calculate({
          macro: macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge,
          discounts,
          stake,
        });

        dispatch(setSnapshot(initialSnapshot));
        console.log('[ValuationStoreInitializer] ✅ Snapshot inicial criado');
      } catch (error) {
        console.error('[ValuationStoreInitializer] ❌ Erro ao criar snapshot inicial:', error);
      }
    } else {
      console.log('[ValuationStoreInitializer] ✅ Snapshot já existe, pulando inicialização');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]); // Executa quando snapshot muda de null para populated

  return null; // Componente não renderiza nada
}
